export const formatDateTime = (value) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return value;
  }
};

const normalizeText = (value) => {
  if (!value) {
    return '';
  }
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const roundAzucarValue = (value) => (Number.isFinite(value) ? Number(value.toFixed(2)) : 0);

const splitAzucarGlassRecipes = (value, maxPerRecipe = 5) => {
  if (!Number.isFinite(value) || value <= 0) {
    return [];
  }
  const batches = [];
  let remaining = roundAzucarValue(value);
  let safety = 0;
  while (remaining > 0 && safety < 20) {
    const chunk = Math.min(maxPerRecipe, remaining);
    const roundedChunk = roundAzucarValue(chunk);
    if (roundedChunk <= 0) {
      break;
    }
    batches.push(roundedChunk);
    remaining = roundAzucarValue(remaining - roundedChunk);
    safety += 1;
  }
  return batches;
};

const resolveAzucarBaseDetail = (details = []) => {
  if (!details.length) {
    return null;
  }
  const byFlag = details.find((detail) => Boolean(detail.es_base_azucar_glass));
  if (byFlag) {
    return byFlag;
  }
  const nonEquivalence = details.find((detail) => !detail?.usar_equivalencia);
  if (nonEquivalence) {
    return nonEquivalence;
  }
  const byName = details.find((detail) => normalizeText(detail?.entidad_nombre).includes('azucar glass'));
  return byName || details[0];
};

const buildAzucarGlassDetails = (details = []) => {
  const azucarDetails = details.filter((detail) => Boolean(detail?.proceso_es_azucar_glass));
  if (!azucarDetails.length) {
    return details;
  }
  const baseDetail = resolveAzucarBaseDetail(azucarDetails);
  if (!baseDetail) {
    return details;
  }
  const equivalenceDetails = azucarDetails.filter((detail) => {
    const equivalence = parseFloat(detail?.equivalencia);
    return Boolean(detail?.usar_equivalencia) && Number.isFinite(equivalence) && equivalence > 0;
  });
  const remainingDetails = details.filter((detail) => !azucarDetails.includes(detail));
  const otherAzucarDetails = azucarDetails.filter((detail) => detail !== baseDetail && !equivalenceDetails.includes(detail));
  const baseQuantity = parseFloat(baseDetail?.cantidad_a_producir || baseDetail?._quantity || 0);
  if (!Number.isFinite(baseQuantity) || baseQuantity <= 0) {
    return details;
  }
  const recipeAmounts = splitAzucarGlassRecipes(baseQuantity);
  if (!recipeAmounts.length) {
    return details;
  }
  const baseUnit = (baseDetail.unidad_articulo || baseDetail.unidades_articulo || 'UNIDADES').toString().toUpperCase();
  const baseName = baseDetail.entidad_nombre || 'Base';
  const baseCategory = baseDetail.categoria || null;
  const baseLinkedArticle = baseDetail.articulo_vinculado || null;

  const totalRecipes = recipeAmounts.length;
  const recipeRows = recipeAmounts.flatMap((amount, index) => {
    const roundedAmount = roundAzucarValue(amount);
    const baseIngredient = {
      name: baseName,
      quantity: roundedAmount,
      displayQuantity: roundedAmount.toFixed(2),
      unitLabel: baseUnit,
      isBase: true
    };
    const otherIngredients = equivalenceDetails.map((detail) => {
      const equivalence = parseFloat(detail?.equivalencia);
      if (!Number.isFinite(equivalence) || equivalence <= 0) {
        return null;
      }
      const quantity = roundAzucarValue(roundedAmount * equivalence);
      if (quantity <= 0) {
        return null;
      }
      const unitLabel = (detail.unidad_articulo || detail.unidades_articulo || 'UNIDADES').toString().toUpperCase();
      return {
        name: detail.entidad_nombre || '',
        quantity,
        displayQuantity: quantity.toFixed(2),
        unitLabel
      };
    }).filter(Boolean);
    const ingredients = [baseIngredient, ...otherIngredients];

    const recipeRow = {
      ...baseDetail,
      entidad_nombre: `Receta #${index + 1}`,
      cantidad_pronosticada: roundedAmount,
      cantidad_ajustada: roundedAmount,
      cantidad_a_producir: roundedAmount,
      stock_capturado: null,
      unidad_articulo: baseUnit,
      unidades_articulo: baseUnit,
      categoria: baseCategory,
      articulo_vinculado: baseLinkedArticle,
      base_nombre: baseName,
      kind: 'recipe',
      ingredients
    };

    if (!ingredients.length) {
      return [recipeRow];
    }

    const ingredientRow = {
      ...baseDetail,
      entidad_nombre: '',
      cantidad_pronosticada: null,
      cantidad_ajustada: null,
      cantidad_a_producir: 0,
      stock_capturado: null,
      unidad_articulo: baseUnit,
      unidades_articulo: baseUnit,
      categoria: baseCategory,
      articulo_vinculado: baseLinkedArticle,
      base_nombre: baseName,
      kind: 'ingredients',
      ingredients
    };

    return [recipeRow, ingredientRow];
  });

  return [...recipeRows, ...otherAzucarDetails, ...remainingDetails];
};

export const groupDetailRecords = (details = []) => {
  const isRecipe = details.some((detail) => detail.entidad_tipo === 'receta' || detail.tipo === 'receta');
  const hasAzucarGlass = details.some((detail) => Boolean(detail?.proceso_es_azucar_glass));
  const groups = new Map();
  let total = 0;

  // Las coberturas representan la hora local del restaurante, no un instante
  // que deba convertirse usando la zona horaria del dispositivo.
  const parseCoverageClock = (value) => {
    if (!value) return null;

    const text = String(value).trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, year, month, day, hours, minutes, seconds = '00'] = match;
      return {
        hours,
        minutes,
        comparableTime: Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hours),
          Number(minutes),
          Number(seconds)
        )
      };
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
      hours: date.getHours().toString().padStart(2, '0'),
      minutes: date.getMinutes().toString().padStart(2, '0'),
      comparableTime: date.getTime()
    };
  };

  const formatClock = (parsedClock) => {
    if (!parsedClock) return '-';
    return `${parsedClock.hours}:${parsedClock.minutes}`;
  };

  const formatDuration = (minutes = 0) => {
    const mins = Math.max(0, Math.round(minutes));
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hrs && rem) return `${hrs}h ${rem}m`;
    if (hrs) return `${hrs}h`;
    return `${rem}m`;
  };

  const computeCoverage = (detail) => {
    if (!detail?.hora_inicial_cobertura || !detail?.hora_final_cobertura) {
      return { coverageRangeText: '-', coverageSpanText: '-', lifespanText: formatDuration(detail?.vida_util_min || detail?.minutos || 0) };
    }
    const start = parseCoverageClock(detail.hora_inicial_cobertura);
    const end = parseCoverageClock(detail.hora_final_cobertura);
    if (!start || !end) {
      return { coverageRangeText: '-', coverageSpanText: '-', lifespanText: formatDuration(detail?.vida_util_min || detail?.minutos || 0) };
    }
    const diffMinutes = Math.max(0, Math.round((end.comparableTime - start.comparableTime) / 60000));
    const coverageSpanText = formatDuration(diffMinutes);
    const coverageRangeText = `${formatClock(start)} - ${formatClock(end)}`;
    const lifespanText = formatDuration(detail?.vida_util_min || detail?.minutos || 0);
    return { coverageRangeText, coverageSpanText, lifespanText };
  };

  const normalizedDetails = hasAzucarGlass ? buildAzucarGlassDetails(details) : details;

  normalizedDetails.forEach((detail) => {
    const rawQuantity = parseFloat(detail.cantidad_a_producir || detail._quantity || 0);
    const quantity = detail.kind === 'ingredients' ? 0 : Math.max(0, Number.isFinite(rawQuantity) ? rawQuantity : 0);
    const key = isRecipe ? detail.articulo_vinculado || 'Sin artículo vinculado' : detail.categoria || 'Sin categoría';
    const coverage = computeCoverage(detail);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    const isAzucarGlass = Boolean(detail.proceso_es_azucar_glass);
    groups.get(key).push({ ...detail, _quantity: quantity, isAzucarGlass, ...coverage });
    if (detail.kind !== 'ingredients') {
      total += quantity;
    }
  });

  const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
    const aSin = a.startsWith('Sin');
    const bSin = b.startsWith('Sin');
    if (aSin && !bSin) return 1;
    if (!aSin && bSin) return -1;
    return a.localeCompare(b);
  });

  const groupedRows = sortedKeys.map((key) => {
    const rows = groups.get(key) || [];
    const subtotal = rows.reduce((sum, row) => sum + (row.kind !== 'ingredients' ? row._quantity : 0), 0);
    return { key, rows, subtotal };
  });

  return {
    isRecipe,
    isAzucarGlass: hasAzucarGlass,
    total,
    groups: groupedRows
  };
};

const ensureExportContainer = (container) => {
  if (typeof document === 'undefined') {
    return null;
  }
  let target = container;
  if (!target || !(target instanceof HTMLElement)) {
    target = document.getElementById('export-document');
  }
  if (!target) {
    target = document.createElement('div');
    document.body.appendChild(target);
  }
  target.id = 'export-document';
  target.style.position = 'fixed';
  target.style.top = '-99999px';
  target.style.left = '-99999px';
  target.style.zIndex = '0';
  target.innerHTML = '';
  return target;
};

export const buildExportDocument = ({
  container,
  header,
  detailGroups,
  verificationUrl = ''
}) => {
  const target = ensureExportContainer(container);
  if (!target || !header || !detailGroups) {
    return null;
  }

  const page = document.createElement('div');
  page.className = 'export-page';

  const exportHeader = document.createElement('div');
  exportHeader.className = 'export-header';

  const brand = document.createElement('div');
  brand.className = 'export-brand';
  const logo = document.createElement('img');
  logo.src = '/static/images/logo.png';
  logo.alt = 'Tim Hortons';
  brand.appendChild(logo);

  const titleWrap = document.createElement('div');
  const title = document.createElement('h1');
  title.className = 'export-title';
  title.textContent = 'Orden de Producción';
  titleWrap.appendChild(title);

  brand.appendChild(titleWrap);

  const meta = document.createElement('div');
  meta.className = 'export-meta';
  meta.innerHTML = `
    <div><strong>Restaurante</strong><span>${header.sucursal_nombre || '-'}</span></div>
    <div><strong>Fecha/Hora</strong><span>${formatDateTime(header.created_at)}</span></div>
    <div><strong>Folio</strong><span>FH-${header.id}</span></div>
    ${header.snapshot_uuid ? `<div><span class="export-badge">SNAP ${header.snapshot_uuid}</span></div>` : ''}
  `;

  exportHeader.appendChild(brand);
  exportHeader.appendChild(meta);
  page.appendChild(exportHeader);

  if (verificationUrl && window.QRCode && typeof window.QRCode.toCanvas === 'function') {
    try {
      const qrWrap = document.createElement('div');
      qrWrap.className = 'export-qr';
      const qrCanvas = document.createElement('canvas');
      window.QRCode.toCanvas(qrCanvas, verificationUrl, { width: 96 }, () => {
        const label = document.createElement('div');
        label.textContent = 'Verificación';
        qrWrap.appendChild(qrCanvas);
        qrWrap.appendChild(label);
      });
      page.appendChild(qrWrap);
    } catch (error) {
      console.warn('QR code generation failed:', error);
    }
  }

  const formatQuantity = (value, useDecimals) => {
    if (value === null || value === undefined) {
      return '';
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    return useDecimals ? parsed.toFixed(2) : Math.round(parsed).toString();
  };

  const formatSubtotal = (value, useDecimals) => (
    useDecimals ? value.toFixed(2) : Math.round(value).toString()
  );

  detailGroups.groups.forEach((group) => {
    const section = document.createElement('div');
    section.className = 'modal-group';
    const groupTitle = document.createElement('h3');
    groupTitle.className = 'export-group-title';
    groupTitle.textContent = `${detailGroups.isRecipe ? 'Artículo vinculado' : 'Categoría'}: ${group.key}`;
    section.appendChild(groupTitle);

    const table = document.createElement('table');
    table.className = 'export-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>Producto</th>
          <th style="width:110px">Vida útil</th>
          <th style="width:120px">Forecast</th>
          <th style="width:140px">Horario forecast</th>
          <th style="width:110px">Unidad</th>
          <th class="qty" style="width:110px">Pronosticado</th>
          <th class="qty" style="width:110px">Stock</th>
          <th class="qty" style="width:110px">A Producir</th>
        </tr>
      </thead>
    `;
    const tbody = document.createElement('tbody');
    table.appendChild(tbody);

    group.rows
      .filter((row) => row.kind === 'ingredients' || row._quantity > 0)
      .forEach((row) => {
        const tr = document.createElement('tr');
        if (row.kind === 'ingredients') {
          const ingredientsText = (row.ingredients || [])
            .map((ingredient) => `${ingredient.name}: ${ingredient.displayQuantity} ${ingredient.unitLabel}`)
            .join(' · ');
          tr.className = 'export-ingredient-row';
          tr.innerHTML = `
            <td colspan="8">
              <span class="export-ingredient-label">Ingredientes</span>
              <span class="export-ingredient-text">${ingredientsText || '-'}</span>
            </td>
          `;
          tbody.appendChild(tr);
          return;
        }

        const isAzucarGlass = Boolean(row.isAzucarGlass);
        const unitLabel = (row.unidad_articulo || row.unidades_articulo || 'UNIDADES').toString().toUpperCase();
        const nameCell = row.kind === 'recipe'
          ? `<div class="export-recipe-name">${row.entidad_nombre || ''}</div><div class="export-recipe-base">Base: ${row.base_nombre || ''}</div>`
          : (row.entidad_nombre || '');
        const forecastValue = formatQuantity(row.cantidad_pronosticada, isAzucarGlass);
        const stockValue = row.stock_capturado !== null && row.stock_capturado > 0 ? row.stock_capturado : '';
        const toProduceValue = formatQuantity(row._quantity, isAzucarGlass);
        tr.className = row.kind === 'recipe' ? 'export-recipe-row' : '';
        tr.innerHTML = `
          <td>${nameCell}</td>
          <td>${row.lifespanText || '-'}</td>
          <td>${row.coverageSpanText || '-'}</td>
          <td>${row.coverageRangeText || '-'}</td>
          <td>${unitLabel}</td>
          <td class="qty"><strong>${forecastValue}</strong></td>
          <td class="qty"><strong>${stockValue}</strong></td>
          <td class="qty"><strong>${toProduceValue}</strong></td>
        `;
        tbody.appendChild(tr);
      });

    section.appendChild(table);
    const subtotal = document.createElement('div');
    subtotal.className = 'export-totals';
    const subtotalValue = typeof group.subtotal === 'number'
      ? group.subtotal
      : group.rows.reduce((sum, row) => sum + (row.kind !== 'ingredients' ? row._quantity : 0), 0);
    subtotal.textContent = `Subtotal ${group.key}: ${formatSubtotal(subtotalValue, detailGroups.isAzucarGlass)}`;
    section.appendChild(subtotal);
    page.appendChild(section);
  });

  const footer = document.createElement('div');
  footer.className = 'export-footer';
  ['Elaboró', 'Revisó', 'Autorizó'].forEach((label) => {
    const sign = document.createElement('div');
    sign.className = 'export-sign';
    sign.innerHTML = '<div class="line"></div>' + `<div class="label">${label}</div>`;
    footer.appendChild(sign);
  });
  page.appendChild(footer);

  target.appendChild(page);
  return target;
};

const renderCanvasFromContainer = async (container) => {
  const html2canvas = window.html2canvas;
  if (typeof html2canvas !== 'function') {
    throw new Error('html2canvas no está disponible');
  }
  return html2canvas(container, { backgroundColor: '#ffffff', scale: 2.5, useCORS: true });
};

export const exportDocumentFile = async ({
  container,
  header,
  detailGroups,
  verificationUrl,
  format
}) => {
  const target = buildExportDocument({ container, header, detailGroups, verificationUrl });
  if (!target) {
    throw new Error('No se pudo preparar el documento para exportar');
  }
  const canvas = await renderCanvasFromContainer(target);
  const baseName = `orden_produccion_${header?.sucursal_nombre || 'restaurante'}_FH-${header?.id || 'sin-id'}`;

  if (format === 'jpg') {
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${baseName}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const { jsPDF } = window.jspdf || {};
  if (typeof jsPDF !== 'function') {
    throw new Error('jsPDF no está disponible');
  }
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
  pdf.save(`${baseName}.pdf`);
};
